/**
 * Word document view.
 *
 * Laid out as a continuous page rather than paginated. Word's page breaks
 * depend on the exact font metrics of the machine that wrote the file, so any
 * page number this viewer drew would be a guess presented as a fact — §16
 * warns specifically that page breaks may differ, and §19 forbids dressing an
 * approximation up as the real thing. A continuous column is honest about
 * being a rendering rather than a reproduction.
 *
 * Editing works one paragraph at a time. Click a paragraph, it becomes a text
 * box; commit and the change is queued for a part-preserving save. Paragraphs
 * the save path cannot rebuild — pictures, fields, tracked changes — do not
 * offer the affordance at all, because an Edit button that loses content is
 * worse than no Edit button.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { imageSizePx } from '@moduviewer/handlers';
import type {
  DocxBlock, DocxModel, DocxParagraph, DocxRun, DocxTable,
} from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface DocxViewerProps {
  readonly model: DocxModel;
  readonly editing: boolean;
  readonly search: string;
  readonly zoom: number;
  readonly onEditParagraph: (index: number, text: string) => void;
}

export function DocxViewer({
  model, editing, search, zoom, onEditParagraph,
}: DocxViewerProps): React.JSX.Element {
  const [active, setActive] = useState<number | null>(null);

  // Object URLs are per-picture and released together when the document
  // changes; leaking them would hold the decoded bytes alive for the session.
  const imageUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const [part, image] of model.images) {
      urls.set(part, URL.createObjectURL(new Blob([image.bytes as BlobPart], { type: image.contentType })));
    }
    return urls;
  }, [model.images]);

  useEffect(() => () => {
    for (const url of imageUrls.values()) URL.revokeObjectURL(url);
  }, [imageUrls]);

  useEffect(() => { if (!editing) setActive(null); }, [editing]);

  return (
    <div className="canvas doc-canvas">
      <article
        className="doc-page"
        style={{ ['--doc-zoom' as string]: String(zoom) }}
        aria-label="문서 내용"
      >
        {model.blocks.map((block, i) => (
          <Block
            key={blockKey(block, i)}
            block={block}
            editing={editing}
            active={active}
            search={search}
            imageUrls={imageUrls}
            edits={model.edits}
            onActivate={setActive}
            onCommit={(index, text) => { setActive(null); onEditParagraph(index, text); }}
            onCancel={() => setActive(null)}
          />
        ))}
      </article>
    </div>
  );
}

function blockKey(block: DocxBlock, position: number): string {
  return block.kind === 'paragraph' ? `p${block.index}` : `t${position}-${block.start}`;
}

/* ------------------------------------------------------------------ *
 * blocks
 * ------------------------------------------------------------------ */

interface BlockProps {
  readonly block: DocxBlock;
  readonly editing: boolean;
  readonly active: number | null;
  readonly search: string;
  readonly imageUrls: ReadonlyMap<string, string>;
  readonly edits: ReadonlyMap<number, string>;
  readonly onActivate: (index: number) => void;
  readonly onCommit: (index: number, text: string) => void;
  readonly onCancel: () => void;
}

function Block(props: BlockProps): React.JSX.Element {
  if (props.block.kind === 'table') return <Table {...props} block={props.block} />;
  return <Paragraph {...props} block={props.block} />;
}

function Table({ block, ...rest }: BlockProps & { block: DocxTable }): React.JSX.Element {
  return (
    <div className="doc-table-wrap">
      <table className="doc-table">
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                cell.merged ? null : (
                  <td key={c} colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}>
                    {cell.paragraphs.map((p) => (
                      <Paragraph key={p.index} {...rest} block={p} />
                    ))}
                  </td>
                )
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Paragraph(
  { block, editing, active, search, imageUrls, edits, onActivate, onCommit, onCancel }:
  BlockProps & { block: DocxParagraph },
): React.JSX.Element {
  const paragraph = block;
  const isActive = active === paragraph.index;
  const changed = edits.has(paragraph.index);
  const canEdit = editing && paragraph.editable;

  if (isActive) {
    return (
      <ParagraphEditor
        paragraph={paragraph}
        onCommit={(text) => onCommit(paragraph.index, text)}
        onCancel={onCancel}
      />
    );
  }

  const Tag = (paragraph.headingLevel ? `h${Math.min(paragraph.headingLevel + 1, 6)}` : 'p') as 'p';
  const empty = paragraph.text.trim() === '' && paragraph.runs.length === 0;

  const body = (
    <Tag
      className={[
        'doc-p',
        paragraph.headingLevel ? 'doc-p--heading' : '',
        paragraph.listLevel !== null ? 'doc-p--list' : '',
        changed ? 'doc-p--changed' : '',
        canEdit ? 'doc-p--editable' : '',
        empty ? 'doc-p--empty' : '',
      ].filter(Boolean).join(' ')}
      style={{
        textAlign: paragraph.alignment ?? undefined,
        marginInlineStart: paragraph.listLevel ? `${paragraph.listLevel * 20}px` : undefined,
      }}
      data-paragraph={paragraph.index}
      {...(canEdit ? {
        role: 'button' as const,
        tabIndex: 0,
        title: '클릭하면 이 문단을 편집합니다',
        onClick: () => onActivate(paragraph.index),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(paragraph.index); }
        },
      } : {})}
    >
      {paragraph.listLevel !== null ? (
        <span className="doc-bullet" aria-hidden="true">
          {paragraph.listOrdered ? '•' : '•'}
        </span>
      ) : null}
      {paragraph.runs.map((run, i) => (
        <Run key={i} run={run} search={search} imageUrls={imageUrls} />
      ))}
      {empty ? ' ' : null}
    </Tag>
  );

  // §19 again: when editing is on and this paragraph cannot take a text edit,
  // say why rather than letting the click do nothing.
  if (editing && !paragraph.editable && !empty) {
    return (
      <div className="doc-locked" title="그림·필드·변경 내용이 있어 텍스트 편집을 지원하지 않습니다">
        {body}
        <Icon name="lock" size={13} className="doc-locked__mark" />
      </div>
    );
  }
  return body;
}

function ParagraphEditor(
  { paragraph, onCommit, onCancel }: {
    paragraph: DocxParagraph;
    onCommit: (text: string) => void;
    onCancel: () => void;
  },
): React.JSX.Element {
  const [text, setText] = useState(paragraph.text);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  return (
    <div className="doc-edit">
      <textarea
        ref={area}
        className="doc-edit__area"
        value={text}
        aria-label={`문단 ${paragraph.index + 1} 편집`}
        onChange={(e) => {
          setText(e.target.value);
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          // Enter commits; Shift+Enter inserts the line break that becomes
          // `<w:br/>` on save.
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(text); }
        }}
        onBlur={() => onCommit(text)}
      />
      <p className="doc-edit__hint">
        <kbd>Enter</kbd> 적용 · <kbd>Shift</kbd>+<kbd>Enter</kbd> 줄바꿈 · <kbd>Esc</kbd> 취소
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * runs
 * ------------------------------------------------------------------ */

function Run(
  { run, search, imageUrls }: {
    run: DocxRun;
    search: string;
    imageUrls: ReadonlyMap<string, string>;
  },
): React.JSX.Element | null {
  if (run.kind === 'break') return <br />;
  if (run.kind === 'tab') return <span className="doc-tab" aria-hidden="true" />;

  if (run.kind === 'field') {
    return <sup className="doc-note" title={run.instruction}>{run.text}</sup>;
  }

  if (run.kind === 'image') {
    const url = run.part ? imageUrls.get(run.part) : undefined;
    const { width, height } = imageSizePx(run.widthEmu, run.heightEmu);
    if (!url) {
      return (
        <span className="doc-image doc-image--missing" style={{ width, height }}>
          <Icon name="file-image" size={18} />
          <span>{run.alt || '표시할 수 없는 그림'}</span>
        </span>
      );
    }
    return <img className="doc-image" src={url} alt={run.alt} width={width} height={height} />;
  }

  const style: React.CSSProperties = {
    fontWeight: run.bold ? 700 : undefined,
    fontStyle: run.italic ? 'italic' : undefined,
    textDecoration: [
      run.underline ? 'underline' : '',
      run.strike ? 'line-through' : '',
    ].filter(Boolean).join(' ') || undefined,
    color: run.color ? `#${run.color}` : undefined,
    backgroundColor: run.highlight ? highlightColor(run.highlight) : undefined,
    verticalAlign: run.vertical === 'super' ? 'super' : run.vertical === 'sub' ? 'sub' : undefined,
    fontSize: run.vertical ? '0.75em' : undefined,
  };

  const content = search ? highlight(run.text, search) : run.text;

  if (run.href) {
    return (
      // Links are shown and copyable but never followed from inside a
      // document — the shell opens them, after the user asks (§29, §30).
      <a
        className="doc-link"
        href={run.href}
        style={style}
        title={run.href}
        onClick={(e) => e.preventDefault()}
      >
        {content}
      </a>
    );
  }

  if (run.revision) {
    return (
      <span
        className={run.revision === 'inserted' ? 'doc-ins' : 'doc-del'}
        style={style}
        title={run.revision === 'inserted' ? '삽입된 내용' : '삭제된 내용'}
      >
        {content}
      </span>
    );
  }

  return <span style={style}>{content}</span>;
}

/** Word stores highlights as named colours, not values. */
function highlightColor(name: string): string {
  const named: Readonly<Record<string, string>> = {
    yellow: '#fff29a', green: '#b6f0b6', cyan: '#a9ecf5', magenta: '#f5b8e8',
    blue: '#b4c9f7', red: '#f7b4b4', darkYellow: '#e3d08a', lightGray: '#e2e0dc',
  };
  return named[name] ?? '#fff29a';
}

function highlight(text: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (;;) {
    const found = lower.indexOf(target, cursor);
    if (found < 0) break;
    if (found > cursor) out.push(text.slice(cursor, found));
    out.push(<mark key={found}>{text.slice(found, found + needle.length)}</mark>);
    cursor = found + needle.length;
  }
  if (out.length === 0) return text;
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
